/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import {
	dismissFlag as createDismissFlag,
	type FlagComponentProps,
	SuccessFlag,
	useFlagService,
} from '@atlassian/jira-flags';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { generateId } from '../../utils/index.tsx';

const title = defineMessage({
	id: 'cross-join-nudges.request-access-success-flag.title',
	defaultMessage: 'A request was sent to your admins',
	description: 'Title of invite success flag after user has requested to join Confluence',
});

const description = defineMessage({
	id: 'cross-join-nudges.request-access-success-flag.description',
	defaultMessage: "You'll receive an email once they've reviewed it.",
	description: 'Description informing the user their request has been successfully sent',
});

export const useRequestAccessSuccessFlag = (): (() => void) => {
	const { showFlag, dismissFlag } = useFlagService();
	return useCallback(() => {
		const id = `success-${generateId()}`;
		const dismiss = () => dismissFlag(createDismissFlag(id));

		showFlag({
			id,
			render: (props) => <RequestAccessSuccessFlag {...props} id={id} onDismiss={dismiss} />,
		});
	}, [dismissFlag, showFlag]);
};

export type Props = {
	id: FlagComponentProps['id'];
	onDismiss: FlagComponentProps['onDismissed'];
};

export const RequestAccessSuccessFlag = ({ onDismiss, ...props }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<SuccessFlag
			{...props}
			onDismissed={onDismiss}
			title={formatMessage(title)}
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			description={<p>{formatMessage(description)}</p>}
		/>
	);
};
